/**
 * Tipos de documento clínico (UI) ↔ valores da API (/documents).
 */

export type ClinicalDocumentUiType = 'PRESCRIPTION' | 'CERTIFICATE' | 'RECIPE' | 'EXAM';

export type ClinicalDocumentApiType = 'prescription' | 'certificate' | 'report' | 'recipe';

const UI_TO_API: Record<ClinicalDocumentUiType, ClinicalDocumentApiType> = {
  PRESCRIPTION: 'prescription',
  RECIPE: 'recipe',
  CERTIFICATE: 'certificate',
  EXAM: 'report',
};

const API_TO_LABEL: Record<string, string> = {
  prescription: 'Prescrição',
  PRESCRIPTION: 'Prescrição',
  receita: 'Prescrição',
  recipe: 'Receituário',
  RECIPE: 'Receituário',
  certificate: 'Atestado',
  CERTIFICATE: 'Atestado',
  atestado: 'Atestado',
  report: 'Pedido de Exame / Laudo',
  REPORT: 'Pedido de Exame / Laudo',
  exam: 'Pedido de Exame / Laudo',
  EXAM: 'Pedido de Exame / Laudo',
  exame: 'Pedido de Exame / Laudo',
};

export function toClinicalDocumentApiType(uiType: ClinicalDocumentUiType): ClinicalDocumentApiType {
  return UI_TO_API[uiType];
}

export function clinicalDocumentLabel(type: string): string {
  if (!type) return 'Documento';
  return API_TO_LABEL[type] ?? API_TO_LABEL[type.toLowerCase()] ?? 'Documento';
}

/**
 * Título amigável para listagens do paciente (dashboard / recentes).
 * Prefere fileName legível; senão label canônico + médico.
 */
export function patientDocumentDisplayName(doc: {
  documentType?: string;
  fileName?: string | null;
  appointment?: {
    doctor?: {
      name?: string;
      user?: { name?: string };
    };
  };
}): string {
  const typeLabel = clinicalDocumentLabel(doc.documentType || '');
  const rawFile = (doc.fileName || '').trim();
  const looksGeneric =
    !rawFile ||
    /^[0-9a-f-]{36}/i.test(rawFile) ||
    rawFile.toLowerCase() === 'document.pdf' ||
    rawFile.toLowerCase() === 'documento.pdf';

  if (!looksGeneric) {
    return rawFile.replace(/\.pdf$/i, '');
  }

  const doctor = documentDoctorName(doc);
  if (doctor && doctor !== 'Médico') {
    return `${typeLabel} · ${doctor}`;
  }
  return typeLabel;
}

/** Template types no backend são minúsculos (prescription/certificate/recipe). */
export function templateTypesForClinicalUi(uiType: ClinicalDocumentUiType): string[] {
  if (uiType === 'CERTIFICATE') return ['certificate', 'CERTIFICATE'];
  if (uiType === 'EXAM') return ['report', 'REPORT', 'exam', 'EXAM'];
  if (uiType === 'RECIPE') return ['recipe', 'RECIPE', 'prescription', 'PRESCRIPTION'];
  return ['prescription', 'PRESCRIPTION', 'recipe', 'RECIPE'];
}

/** Nome do médico a partir do shape enriquecido ou legado. */
export function documentDoctorName(doc: {
  appointment?: {
    doctor?: {
      name?: string;
      user?: { name?: string };
      specialty?: string;
    };
  };
}): string {
  return (
    doc.appointment?.doctor?.user?.name ||
    doc.appointment?.doctor?.name ||
    'Médico'
  );
}
